import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab, Avatar } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { useSelector } from 'react-redux';
import LogTable from './LogTable';
import SignupJourney from './SignupJourney';
import StatTile from './StatTile';
import { fetchActivityLogsData, fetchUserStatData } from '../../api/user';
import ActivityLogsSkeleton from './ActivityLogsSkeleton';

// While the popup is open both tabs refetch on this beat (skipped while the browser tab is hidden).
const REFRESH_MS = 10000;

const ActivityLogs = ({ userId, data }) => {
  const [activityLogData, setActivityLogData] = useState([]);
  const [totalDuration, setTotalDuration] = useState();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState();
  const [tab, setTab] = useState('journey');
  const { dateRange } = useSelector((state) => state.filter);

  const activityTotals = useMemo(() => {
    return activityLogData.reduce(
      (acc, item) => {
        acc.viewCount += item?.profileView || 0;
        acc.conversationCount += item?.conversation || 0;
        acc.interestedCount += item?.favorite || 0;
        acc.sessionCount += item?.session || 0;
        return acc;
      },
      { viewCount: 0, conversationCount: 0, interestedCount: 0, sessionCount: 0 }
    );
  }, [activityLogData]);

  const fetchData = async () => {
    try {
      await fetchUserStatData(userId, dateRange, setUserData);
      await fetchActivityLogsData(userId, dateRange, setActivityLogData, setTotalDuration);
    } catch (err) {
      console.error('Error while loading user data', err);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => !document.hidden && fetchData(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [userId, dateRange]);

  if (loading) {
    return <ActivityLogsSkeleton />;
  }

  const name = userData?.firstName ?? userData?.userName ?? data?.firstName;
  const photo = data?.profilePicture || userData?.profilePicture;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} sx={{ pr: 5 }}>
        <Avatar src={photo} alt={name} sx={{ width: 48, height: 48, bgcolor: '#F6EDD9', color: '#7A4E00', fontWeight: 700 }}>
          {name?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111', textTransform: 'capitalize' }} noWrap>{name || 'Member'}</Typography>
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: '2px', borderRadius: '12px', fontSize: 12, fontWeight: 600,
              color: userData?.isOnLine ? '#008E40' : '#5F6368', bgcolor: userData?.isOnLine ? '#E6F4EA' : '#F1F3F4',
            }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
              {userData?.isOnLine ? 'Online' : 'Offline'}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#6A7282' }} noWrap>
            {[userData?.userName && `@${userData.userName}`, data?.email].filter(Boolean).join(' · ') || 'Activity Logs'}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{ mt: 2.5, mb: 2.5, borderBottom: '1px solid #ECECEC' }}>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', fontSize: 14, fontWeight: 600, minHeight: 40, px: 1.5, color: '#6A7282' },
          '& .Mui-selected': { color: '#BE1F1F !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#BE1F1F' },
        }}
      >
        <Tab value="journey" label="Signup journey" />
        <Tab value="activity" label="Activity" />
      </Tabs>
        <Box display="flex" alignItems="center" gap={0.75} sx={{ fontSize: 12, color: '#008E40', whiteSpace: 'nowrap' }} title="Refreshes every 10 seconds while open">
          <Box component="span" sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: '#00A63E',
            animation: 'livePulse 2s ease-in-out infinite',
            '@keyframes livePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
          }} />
          Live · every 10s
        </Box>
      </Box>

      {tab === 'activity' ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
            <StatTile tone="#2E80DB" Icon={VisibilityOutlinedIcon} title="Profile views" value={activityTotals.viewCount} />
            <StatTile tone="#6E59D9" Icon={AccessTimeOutlinedIcon} title="Time spent" value={totalDuration || '0 min'} />
            <StatTile tone="#008E40" Icon={ChatBubbleOutlineIcon} title="Conversations" value={activityTotals.conversationCount} />
            <StatTile tone="#D93025" Icon={FavoriteBorderIcon} title="Favorites" value={activityTotals.interestedCount} />
            <StatTile tone="#C77700" Icon={LoginOutlinedIcon} title="Sessions" value={activityTotals.sessionCount} />
          </Box>
          <Box sx={{ mt: 3 }}>
            <LogTable data={activityLogData} />
          </Box>
        </>
      ) : (
        <SignupJourney userId={userId} refreshMs={REFRESH_MS} />
      )}
    </Box>
  );
};

export default ActivityLogs;
